import React, { useContext, useEffect, useState, useCallback, useMemo, Suspense } from "react";
import AccountNavBar from "../../NewComponents/AccountNavBar";
import TopBar from "../../Components/TopBar";
import "./Dashboard.css";

import { ReactComponent as EYE } from "../../assets/icons/Eye-Icon.svg";
import { ReactComponent as PEN } from "../../assets/icons/Pen.svg";
import { ReactComponent as CLOCK } from "../../assets/icons/Clock.svg";
import { ReactComponent as ARROW } from "../../assets/icons/arrow.svg";
import { ReactComponent as DATE } from "../../assets/icons/Date.svg";
import { ReactComponent as DOWN } from "../../assets/icons/filter-down.svg";
import { ReactComponent as UP } from "../../assets/icons/filter-up.svg";


import { ChevronRight } from "react-feather";

import { AuthContext } from "../../Context/AuthContext";
import { firestore } from "../../firebase.config";
import { DataContext } from "../../Context/DataContext";
import { useScreenTransition } from "../../Hooks/useScreenTransition";
import NewButton from "../../NewComponents/NewButton";
import IncomeReport from "./IncomeReport";
import { AccountType } from "../../utility/enums";
import { getDiscount, getTotal } from "../../Data/Functions";
const IncomeChart = React.lazy(() => import("./Charts/incomeChart"));
const OrderCount = React.lazy(() => import("./Charts/orderCountChart"));
const OrderCompleteChart = React.lazy(() => import("./Charts/orderCompleteChart"));
const QuoteCompleteChart = React.lazy(() => import("./Charts/quoteCompleteChart"));
const ClientsChart = React.lazy(() => import("./Charts/ClientsChart"));
const OptionalServices = React.lazy(() => import("./Charts/OptionalServices"));

const Dashboard = ({ windowSize }) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStates, setLoadingStates] = useState({
    users: false,
    quotes: false,
    orders: false
  });
  const [selectedRange, setSelectedRange] = useState({
    label: "Current Month",
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0, 0),
    endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0, 23, 59, 59, 999),
  });
  const [tempSelectedRange, setTempSelectedRange] = useState(selectedRange);

  const { signedIn } = useContext(AuthContext);
  const { userData } = useContext(DataContext);

  const [ordersData, setOrdersData] = useState([]);
  const [totalStatusCounts, setTotalStatusCounts] = useState({
    completedCount: 0,
    notCompletedCount: 0,
  });

  const [totalQuoteStatusCounts, setTotalQuoteStatusCounts] = useState({
    completedCount: 0,
    notCompletedCount: 0,
  });

  const [clientsData, setClientsData] = useState({
    PersonalClients: 0,
    BusinessClients: 0,
  });

  const [optionalServicesData, setOptionalServicesData] = useState({
    hardCopy: 0,
    expedited: 0,
    notarization: 0,
    expeditedPlus: 0,
  });

  const [overdueOrdersCount, setOverdueOrdersCount] = useState(0);
  const [draftOrdersCount, setDraftOrdersCount] = useState(0);
  const [revisionOrdersCount, setRevisionOrdersCount] = useState(0);
  const [showIncomeReport, setShowIncomeReport] = useState(false);
  const { transitionTo } = useScreenTransition();

  function checkDueDate(dueDate) {
    // Convert the input dates to Date objects
    const due = new Date(dueDate);
    const current = new Date(); // Current date and time

    // Check if the due date is before or after the current date
    if (current > due) {
      return true;
    } else {
      return false;
    }
  }

  useEffect(() => {
      if(!signedIn || (userData && userData.role === "client")){
        return transitionTo("/");
      }
    }, [signedIn, userData]);

  useEffect(() => {
    if (signedIn) {
      setLoadingStates(prev => ({ ...prev, users: true }));

      const usersRef = firestore.collection("users");

      const startTimestamp = selectedRange.startDate.getTime();
      const endTimestamp = selectedRange.endDate.getTime();

      const unsubscribe = usersRef
        .where("timestamp", ">=", startTimestamp)
        .where("timestamp", "<=", endTimestamp)
        .onSnapshot((snapshot) => {
          const filteredUsers = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          // Calculate total counts for the time period
          const totalCounts = filteredUsers.reduce(
            (acc, data) => {
              if (data.teamId) {
                acc.PersonalClients += 1;
              } else {
                acc.BusinessClients += 1;
              }
              return acc;
            },
            { PersonalClients: 0, BusinessClients: 0 }
          );

          setClientsData(totalCounts);
          setLoadingStates(prev => ({ ...prev, users: false }));
        }, (error) => {
          console.error("Error fetching users:", error);
          setLoadingStates(prev => ({ ...prev, users: false }));
        });

      // Fetch the filtered data
      return () => unsubscribe();
    }
  }, [signedIn, selectedRange]);

  useEffect(() => {
    if (signedIn) {
      setLoadingStates(prev => ({ ...prev, quotes: true }));

      const quotesRef = firestore.collection("quotes");

      const startTimestamp = selectedRange.startDate.getTime();
      const endTimestamp = selectedRange.endDate.getTime();

      const unsubscribe = quotesRef
        .where("timestamp", ">=", startTimestamp)
        .where("timestamp", "<=", endTimestamp)
        .onSnapshot((snapshot) => {
          const filteredQuotes = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          if (filteredQuotes.length === 0) {
            // No data found, reset counts to 0
            setTotalQuoteStatusCounts({
              completedCount: 0,
              notCompletedCount: 0,
            });
            setLoadingStates(prev => ({ ...prev, quotes: false }));
            return;
          }

          // Calculate total counts for the time period
          const totalCounts = filteredQuotes.reduce(
            (acc, order) => {
              if (order.status.toLowerCase() === "ordered") {
                acc.completedCount += 1;
              } else {
                acc.notCompletedCount += 1;
              }
              return acc;
            },
            { completedCount: 0, notCompletedCount: 0 }
          );

          // Update the totalQuoteStatusCounts state
          setTotalQuoteStatusCounts(totalCounts);
          setLoadingStates(prev => ({ ...prev, quotes: false }));
        }, (error) => {
          console.error("Error fetching quotes:", error);
          setLoadingStates(prev => ({ ...prev, quotes: false }));
        });

      return () => unsubscribe();
    }
  }, [signedIn, selectedRange]);

  useEffect(() => {
    if (signedIn) {
      setLoadingStates(prev => ({ ...prev, orders: true }));

      const ordersRef = firestore.collection("orders");

      const startTimestamp = selectedRange.startDate.getTime();
      const endTimestamp = selectedRange.endDate.getTime();

      // Fetch the filtered data
      const unsubscribe = ordersRef
        .where("timestamp", ">=", startTimestamp)
        .where("timestamp", "<=", endTimestamp)
        .onSnapshot(
          (snapshot) => {
            const filteredOrders = snapshot.docs
              .map((doc) => ({
                id: doc.id,
                ...doc.data(),
              }))
              .filter(
                (order) =>
                  order.status.toLowerCase() !== "cancelled" &&
                  order.status.toLowerCase() !== "created"
              );
            // Calculate total counts for the time period
            const totalCounts = filteredOrders.reduce(
              (acc, order) => {
                if (order.status.toLowerCase() === "completed") {
                  acc.completedCount += 1;
                } else {
                  acc.notCompletedCount += 1;
                }
                return acc;
              },
              { completedCount: 0, notCompletedCount: 0 }
            );

            const overdueCount = filteredOrders.reduce((acc, order) => {
              if (order.due && checkDueDate(order.due)) {
                return acc + 1;
              }
              return acc;
            }, 0);
            setOverdueOrdersCount(overdueCount);

            const draftCount = filteredOrders.reduce((acc, order) => {
              if (order.status.toLowerCase() === "created") {
                return acc + 1;
              }
              return acc;
            }, 0);
            setDraftOrdersCount(draftCount);

            const revisionCount = filteredOrders.reduce((acc, order) => {
              if (order.status.toLowerCase() === "revision") {
                return acc + 1;
              }
              return acc;
            }, 0);
            setRevisionOrdersCount(revisionCount);

            const servicesCounts = filteredOrders.reduce(
              (acc, order) => {
                if (order.hardCopy) {
                  acc.hardCopy += 1;
                } else if (order.expedited) {
                  acc.expedited += 1;
                } else if (order.notarization) {
                  acc.notarization += 1;
                } else if (order.expeditedPlus) {
                  acc.expeditedPlus += 1;
                }
                return acc;
              },
              { hardCopy: 0, expedited: 0, notarization: 0, expeditedPlus: 0 }
            );
            setOptionalServicesData(servicesCounts);
            // Update the totalStatusCounts state
            setTotalStatusCounts(totalCounts);
            // Generate a complete date or month series
            const isSingleMonth =
              selectedRange.startDate.getMonth() ===
              selectedRange.endDate.getMonth();
            const dateSeries = generateDateOrMonthSeries(
              selectedRange.startDate,
              selectedRange.endDate,
              isSingleMonth
            );

            // Group the orders by date or month and calculate totals
            const groupedData = filteredOrders.reduce((acc, order) => {
              const date = new Date(order.timestamp);

              const key = isSingleMonth
                ? formatDate(date) // Format as dd/mm/yyyy
                : `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(
                    2,
                    "0"
                  )}`; // Format as yyyy/mm

              // Initialize counters and totals for the date/month if not already present
              if (!acc[key]) {
                acc[key] = {
                  personalCount: 0,
                  businessCount: 0,
                  personalTotalPrice: 0,
                  businessTotalPrice: 0,
                };
              }

              // Ensure totalPrice is treated as a number
              const totalPrice = (getTotal(order) - getDiscount(order)) || 0;

              // Increment the counts and totals based on type
              if (!order.type || order.type === "personal") {
                acc[key].personalCount += 1;
                acc[key].personalTotalPrice += totalPrice;
              } else {
                acc[key].businessCount += 1;
                acc[key].businessTotalPrice += totalPrice;
              }

              return acc;
            }, {});

            // Merge the grouped data with the date series
            const aggregated = dateSeries.map((period) => ({
              period,
              personalCount: groupedData[period]?.personalCount || 0,
              businessCount: groupedData[period]?.businessCount || 0,
              personalTotalPrice: groupedData[period]?.personalTotalPrice || 0
              ,
              businessTotalPrice: groupedData[period]?.businessTotalPrice || 0
            }));

            setOrdersData(aggregated);
            setLoadingStates(prev => ({ ...prev, orders: false }));
          },
          (error) => {
            console.error("Error fetching orders:", error);
            setLoadingStates(prev => ({ ...prev, orders: false }));
          }
        );

      return () => unsubscribe();
    }
  }, [signedIn, selectedRange]);

  useEffect(() => {
    const isAnyLoading = loadingStates.users || loadingStates.quotes || loadingStates.orders;
    setIsLoading(isAnyLoading);
  }, [loadingStates]);

  // Helper function to generate a date or month series
  const generateDateOrMonthSeries = (startDate, endDate, isSingleMonth) => {
    const series = [];
    const currentDate = new Date(startDate);

    if (isSingleMonth) {
      // Generate daily series
      while (currentDate <= endDate) {
        series.push(formatDate(currentDate)); // Format as dd/mm/yyyy
        currentDate.setDate(currentDate.getDate() + 1);
      }
    } else {
      // Generate monthly series
      while (currentDate <= endDate) {
        const monthKey = `${currentDate.getFullYear()}/${String(
          currentDate.getMonth() + 1
        ).padStart(2, "0")}`; // yyyy/mm
        series.push(monthKey);
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
    }

    return series;
  };

  // Helper function to format date as dd/mm/yyyy
  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const containerItems = useMemo(() => [
    {
      title: "Revision Orders",
      count: revisionOrdersCount,
      icon: <EYE />,
      bgColor: "#DFF4F1",
    },
    {
      title: "Draft Orders",
      count: draftOrdersCount,
      icon: <PEN />,
      bgColor: "#F0F0F0",
    },
    {
      title: "Overdue Invoices",
      count: overdueOrdersCount,
      icon: <CLOCK />,
      bgColor: "#FFEBEB",
    },
  ], [revisionOrdersCount, draftOrdersCount, overdueOrdersCount]);

  const filterLabel = useMemo(() => [
    {
      label: "Current Month",
      value: {
        // Start from the 1st day of the current month
        startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        // End on the last day of the current month
        endDate: new Date(
          new Date().getFullYear(),
          new Date().getMonth() + 1,
          0
        ),
      },
    },
    {
      label: "Last Month",
      value: {
        startDate: new Date(
          new Date().getFullYear(),
          new Date().getMonth() - 1,
          1
        ),
        endDate: new Date(new Date().getFullYear(), new Date().getMonth(), 0),
      },
    },
    {
      label: "Last 6 Months",
      value: {
        startDate: new Date(
          new Date().getFullYear(),
          new Date().getMonth() - 5,
          1
        ),
        endDate: new Date(
          new Date().getFullYear(),
          new Date().getMonth() + 1,
          0
        ),
      },
    },
    {
      label: "Current Year",
      value: {
        startDate: new Date(new Date().getFullYear(), 0, 1),
        endDate: new Date(new Date().getFullYear(), 11, 31),
      },
    },
    {
      label: "Last Year",
      value: {
        startDate: new Date(new Date().getFullYear() - 1, 0, 1),
        endDate: new Date(new Date().getFullYear() - 1, 11, 31),
      },
    },
  ], []);

  const handleApplyFilter = useCallback(() => {
    setSelectedRange(tempSelectedRange);
    setOpen(false);
  }, [tempSelectedRange]);

  const handleSelectRange = useCallback((range) => {
    setTempSelectedRange({ label: range.label, ...range.value });
  }, []);

  const handleResetFilter = useCallback(() => {
    const defaultRange = {
      label: "Current Month",
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
    };
    setTempSelectedRange(defaultRange);
    setSelectedRange(defaultRange);
    setOpen(false);
  }, []);
  return (
    <>
      <TopBar />
      <AccountNavBar
        showButton={true}
        windowSize={windowSize}
        selectedTab="dashboard"
        accountMainPadding={false}
        admin={true}
        bgTransparent={showIncomeReport ? false : true}
      >
        {showIncomeReport ? <IncomeReport showIncomeReport={showIncomeReport} setShowIncomeReport={setShowIncomeReport} /> :
        <div className=" container mx-auto grid gap-4 " style={{ contain: 'layout style paint' }}>
          <div className="mainTitleRow flex justify-between items-center">
            <h2 className="mainTitle">Dashboard</h2>

           {userData && userData.role === AccountType.OWNER && <div className="flex gap-2">
              <NewButton text={"View Income Report"} onClick={() => {setShowIncomeReport(true)}} whiteBtn icon={false} back={true} />
            </div>}
          </div>

          
            <div className="w-full bg-white rounded-lg py-4 px-5 grid grid-cols-1 lg:grid-cols-3 lg:gap-1 gap-4 items-center">
              {containerItems.map((item, index) => (
                <div key={index} className="dashboardContainerTab">
                  <div
                    className="dashboardIcon"
                    style={{ background: item.bgColor }}
                  >
                    {item.icon}
                  </div>
                  <div className="dashboardContent">
                    <span className="dashboardTitle">{item.title}</span>
                    <span className="dashboardCount">
                      {isLoading ? "..." : item.count}
                    </span>
                  </div>
                  <div className="dashboardArrow">
                    <ARROW />
                  </div>
                </div>
              ))}
            </div>

            <div className="dashboardTimeFilterRow">
              <div className="relative">
                <div
                  className={`dashboardTimeFilter border-2 ${
                    !open ? "border-[#e6e7e9]" : "border-[#12ABBF]"
                  }`}
                  onClick={() => setOpen(!open)}
                >
                  <DATE className="dashboardTimeFilterIcon" />
                  <p>{selectedRange.label}</p>
                  {open ? <UP /> : <DOWN />}
                </div>

                {open && (
                  <div className="absolute top-12 bg-white p-4 rounded-lg shadow-md z-10">
                    <div className="grid xl:grid-cols-5 md:grid-cols-3 sm:grid-cols-2 grid-cols-1 items-center gap-4">
                      {filterLabel.map((item, index) => (
                        <div
                          key={index}
                          className={`border-2 font-normal text-base px-4 py-2 rounded-full md:w-full text-center w-fit cursor-pointer ${
                            tempSelectedRange.label === item.label
                              ? "border-[#12ABBF]"
                              : "border-[#e6e7e9]"
                          }`}
                          onClick={() => handleSelectRange(item)}
                        >
                          {item.label}
                        </div>
                      ))}
                    </div>
                    <hr className="my-4" />
                    <div className="flex md:flex-row flex-col justify-between items-center gap-4">
                      <div className="flex md:w-fit w-full items-center font-normal text-center justify-center py-1.5 px-4 rounded-md bg-[#F7F6F7] text-[#686F7B]">
                        {formatDate(tempSelectedRange.startDate)} <ChevronRight />{" "}
                        {formatDate(tempSelectedRange.endDate)}
                      </div>
                      <div className="flex gap-2">
                        <button
                          className="border-2 border-[#F0F0F0]  font-medium text-lg rounded-md text-center px-12 py-1.5"
                          onClick={() => handleResetFilter()}
                        >
                          Clear
                        </button>
                        <button
                          className="bg-[#12ABBF] text-white font-medium text-lg rounded-md text-center px-12 py-1.5"
                          onClick={handleApplyFilter}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {!isLoading ? (
              <>
                <div className="grid gap-4 grid-cols-1 xl:grid-cols-2 w-full ">
                  <Suspense fallback={<div className="dashboardCharts animate-pulse bg-gray-200 h-[300px] rounded-lg"></div>}>
                    <IncomeChart IncomeData={ordersData} />
                  </Suspense>
                  <Suspense fallback={<div className="dashboardCharts animate-pulse bg-gray-200 h-[300px] rounded-lg"></div>}>
                    <OrderCount ordersData={ordersData} />
                  </Suspense>
                </div>

                <div className="grid gap-4 xl:grid-cols-4 md:grid-cols-2 grid-cols-1 w-full">
                  <Suspense fallback={<div className="dashboardCharts animate-pulse bg-gray-200 h-[300px] rounded-lg"></div>}>
                    <OrderCompleteChart data={totalStatusCounts} />
                  </Suspense>
                  <Suspense fallback={<div className="dashboardCharts animate-pulse bg-gray-200 h-[300px] rounded-lg"></div>}>
                    <QuoteCompleteChart data={totalQuoteStatusCounts} />
                  </Suspense>
                  <Suspense fallback={<div className="dashboardCharts animate-pulse bg-gray-200 h-[300px] rounded-lg"></div>}>
                    <ClientsChart data={clientsData} />
                  </Suspense>
                  <Suspense fallback={<div className="dashboardCharts animate-pulse bg-gray-200 h-[300px] rounded-lg"></div>}>
                    <OptionalServices data={optionalServicesData} />
                  </Suspense>
                </div>
              </>
            ) : (
              <>
                <div className="grid gap-4 grid-cols-1 xl:grid-cols-2 w-full">
                  <div className="dashboardCharts animate-pulse bg-gray-200 h-[300px] rounded-lg"></div>
                  <div className="dashboardCharts animate-pulse bg-gray-200 h-[300px] rounded-lg"></div>
                </div>
                <div className="grid gap-4 xl:grid-cols-4 md:grid-cols-2 grid-cols-1 w-full">
                  <div className="dashboardCharts animate-pulse bg-gray-200 h-[300px] rounded-lg"></div>
                  <div className="dashboardCharts animate-pulse bg-gray-200 h-[300px] rounded-lg"></div>
                  <div className="dashboardCharts animate-pulse bg-gray-200 h-[300px] rounded-lg"></div>
                  <div className="dashboardCharts animate-pulse bg-gray-200 h-[300px] rounded-lg"></div>
                </div>
              </>
            )}
        </div>}
      </AccountNavBar>
    </>
  );
};

export default Dashboard;
